import {afterNextRender, Component, DestroyRef, effect, signal} from '@angular/core';
import {Checkbox} from "primeng/checkbox";
import {FloatLabel} from "primeng/floatlabel";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {InputGroup} from "primeng/inputgroup";
import {InputGroupAddon} from "primeng/inputgroupaddon";
import {InputMask} from "primeng/inputmask";
import {InputText} from "primeng/inputtext";
import {Message} from "primeng/message";
import {Slider} from "primeng/slider";
import {environment} from '../../../environments/environment';
import {ReCaptchaV3Service} from 'ngx-captcha';
import {ToastService} from '../../../shared/toast/toast.service';
import {ToastType} from '../../../shared/toast/toast-type.enum';
import {finalize} from 'rxjs';
import {ParkingFormRequest} from './parking-form-request.model';
import {ParkingFormService} from './parking-form.service';
import {GoogleAnalyticsService} from '../../../shared/analytics/google/google-analytics.service';
import {Button} from 'primeng/button';
import {NgIcon, provideIcons} from '@ng-icons/core';
import {tablerHeadset} from '@ng-icons/tabler-icons';
import {
  AnalyticsEventCategory,
  AnalyticsEventName,
  FormEventSendResult
} from '../../../shared/enum/gtm-cookie-consent.enum';
import {PixelService} from '../../../shared/analytics/meta/pixel.service';
import {FormSubmitStatus, PixelEventName} from '../../../shared/analytics/meta/types/pixel.types';
import {MicrosoftClarityEvents} from '../../../shared/analytics/microsoft/types/microsoft-clarity-events.enum';
import {MicrosoftClarityService} from '../../../shared/analytics/microsoft/microsoft-clarity.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

type ParkingFormControls = {
  fullName: FormControl<string>;
  company: FormControl<string>;
  fleetSize: FormControl<number>;
  email: FormControl<string>;
  phone: FormControl<string>;
  consentDataProcessing: FormControl<boolean>;
  consentPrivacyPolicy: FormControl<boolean>;
};

@Component({
  selector: 'app-parking-form',
  imports: [
    Checkbox,
    FloatLabel,
    FormsModule,
    InputGroup,
    InputGroupAddon,
    InputMask,
    InputText,
    Message,
    Slider,
    ReactiveFormsModule,
    Button,
    NgIcon
  ],
  templateUrl: './parking-form.component.html',
  styleUrl: './parking-form.component.scss',
  providers: [provideIcons({
    tablerHeadset
  })]
})
export class ParkingFormComponent {

  protected readonly minFleet = 1;
  protected readonly maxFleet = 1500;
  protected readonly fleetSizeDefault = 30;
  protected readonly step = 1;
  protected readonly telRegex: string | RegExp = '^\\d{3}[\\s-]?\\d{3}[\\s-]?\\d{3}$';
  protected readonly recaptchaSiteKey = environment.recaptcha.siteKey;
  protected readonly privacyPolicyUrl = environment.privacyPolicyUrl;
  private readonly captchaFormId: string = "autopark_contact_form";
  private formEngaged = false;

  protected isProcessing = signal(false);

  constructor(
    private readonly recaptchaService: ReCaptchaV3Service,
    private readonly parkingFormService: ParkingFormService,
    private readonly toastService: ToastService,
    private readonly analyticsService: GoogleAnalyticsService,
    private readonly pixelService: PixelService,
    private readonly microsoftClarityService: MicrosoftClarityService,
    private readonly destroyRef: DestroyRef
  ) {
    effect(() => {
      if (this.isProcessing()) {
        this.contactForm.disable({emitEvent: false});
      } else {
        this.contactForm.enable({emitEvent: false});
      }
    });

    this.registerFormEngagementTracking();
  }

  readonly contactForm = new FormGroup<ParkingFormControls>(<ParkingFormControls>{
    fullName: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(50)],
    }),
    company: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(80)],
    }),
    fleetSize: new FormControl<number>(this.fleetSizeDefault, {
      nonNullable: true,
      validators: Validators.required,
    }),
    email: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email, Validators.maxLength(50)],
    }),
    phone: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.pattern(this.telRegex), Validators.maxLength(50)],
    }),
    consentDataProcessing: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: Validators.requiredTrue
    }),
    consentPrivacyPolicy: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: Validators.requiredTrue
    })
  });

  formValueAccessor(name: keyof ParkingFormControls) {
    return this.contactForm.controls[name];
  }

  isInvalid(name: keyof ParkingFormControls) {
    const ctrl = this.formValueAccessor(name);
    return (ctrl.touched || this.isProcessing()) && ctrl.invalid;
  }

  isError(name: keyof ParkingFormControls): string | null {
    const form = this.formValueAccessor(name);
    if (!(form.touched || this.isProcessing()) || form.valid) return null;
    if (form.hasError('required')) return $localize`:@@form.validation-error.required:Pole jest wymagane`;
    if (form.hasError('minlength')) return $localize`:@@form.validation-error.min-length:Za krótka wartość`;
    if (form.hasError('maxlength')) return $localize`:@@form.validation-error.max-length:Za długa wartość`;

    return $localize`:@@form.validation-error.invalid-value:Nieprawidłowa wartość`;
  }

  protected onSubmit() {
    this.isProcessing.set(true);
    this.toastService.fireToast(ToastType.INFO, $localize`:@@autopark.form.toast.title:Zapytanie`, $localize`:@@autopark.form.toast.info-content:Wysyłanie zapytania`);

    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    if (this.contactForm.invalid) return;

    this.recaptchaService.execute(this.recaptchaSiteKey, this.captchaFormId, (token) => {

      const source = this.contactForm;

      const payload: ParkingFormRequest = {
        fullName: source.controls.fullName.getRawValue(),
        company: source.controls.company.getRawValue(),
        fleet: source.controls.fleetSize.getRawValue(),
        email: source.controls.email.getRawValue(),
        phone: source.controls.phone.getRawValue(),
        consentDataProcessing: source.controls.consentDataProcessing.getRawValue(),
        consentPrivacyPolicy: source.controls.consentPrivacyPolicy.getRawValue(),
        recaptchaToken: token
      };

      this.parkingFormService.sendContactMessage(payload).pipe(
        finalize(() => this.isProcessing.set(false))
      ).subscribe({
        next: (response) => {
          if (response.ok) {
            this.toastService.fireToast(ToastType.SUCCESS, $localize`:@@autopark.form.toast.title:Zapytanie`, $localize`:@@autopark.form.toast.success-content:Zapytanie wysłane`);
            this.analyticsService.trackEvent({
              event: AnalyticsEventName.AutoparkContactFormSubmit,
              eventCategory: AnalyticsEventCategory.ContactForm,
              eventPayload: FormEventSendResult.Success
            });

            this.pixelService.trackEvent(
              PixelEventName.ParkingContactFormSubmit, {
                status: FormSubmitStatus.SUCCESS
              });

            this.microsoftClarityService.trackEvent(
              MicrosoftClarityEvents.AutoparkContactFormSubmit
            );

            this.microsoftClarityService.upgradeSession(MicrosoftClarityEvents.AutoparkContactFormSubmit)
          }
          this.contactForm.reset();
        },
        error: () => {
          this.toastService.fireToast(ToastType.ERROR, $localize`:@@autopark.form.toast.title:Zapytanie`, $localize`:@@autopark.form.toast.error-content:Błąd w wysyłaniu zapytania`);
          this.analyticsService.trackEvent({
            event: AnalyticsEventName.AutoparkContactFormSubmit,
            eventCategory: AnalyticsEventCategory.ContactForm,
            eventPayload: FormEventSendResult.Error
          });

          this.pixelService.trackEvent(
            PixelEventName.ParkingContactFormSubmit, {
              status: FormSubmitStatus.SUCCESS
            });

          this.microsoftClarityService.trackEvent(
            MicrosoftClarityEvents.AutoparkContactFormError
          );

          this.microsoftClarityService.upgradeSession(MicrosoftClarityEvents.AutoparkContactFormError)
        }
      });
    });
  }

  private registerFormEngagementTracking(): void {
    afterNextRender(() => {
      this.contactForm.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (this.formEngaged) return;

          this.microsoftClarityService.trackEvent(
            MicrosoftClarityEvents.AutoparkContactFormTouch
          );

          this.microsoftClarityService.upgradeSession(MicrosoftClarityEvents.AutoparkContactFormTouch)
        })
    })
  }
}
